import { useEffect, useState, type FormEvent } from "react";
import { SkillsInput } from "../components/SkillsInput";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, type Contact, type Currency, type Job, type JobInput, type TeamUser, type Terms } from "../lib/api";
import { describeFee } from "./TermsSection";
import { displaySkill } from "../lib/format";

/**
 * Job details for create and edit. On create, the fee comes from the client's current terms
 * unless overridden; with no terms recorded, a fee has to be entered.
 */
export function JobForm({
  companyId,
  job,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  companyId: string;
  job?: Job;
  submitLabel: string;
  onSubmit: (input: JobInput) => Promise<void>;
  onCancel?: () => void;
}) {
  const { pending, error, setError, run } = useAction();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [team, setTeam] = useState<TeamUser[]>([]);
  const [terms, setTerms] = useState<Terms | null | undefined>(undefined);
  const [colleagueJobs, setColleagueJobs] = useState<Job[]>([]);

  const [title, setTitle] = useState(job?.title ?? "");
  const [location, setLocation] = useState(job?.location ?? "");
  const [skills, setSkills] = useState<string[]>(job?.skills ?? []);
  const [knownSkills, setKnownSkills] = useState<string[]>([]);
  const [salaryMin, setSalaryMin] = useState(job?.salaryMin?.toString() ?? "");
  const [salaryMax, setSalaryMax] = useState(job?.salaryMax?.toString() ?? "");
  const [currency, setCurrency] = useState<Currency>(job?.currency ?? "GBP");
  const [hiringContactId, setHiringContactId] = useState(job?.hiringContact?.id ?? "");
  const [ownerId, setOwnerId] = useState(job?.owner.id ?? "");
  const [notes, setNotes] = useState(job?.notes ?? "");
  const [description, setDescription] = useState(job?.description ?? "");
  const [found, setFound] = useState<string[] | null>(null);
  const picking = useAction();
  // Fee: on create, follow the terms unless the user chooses to set it.
  const [customFee, setCustomFee] = useState(job ? true : false);
  const [feeType, setFeeType] = useState<"percent" | "fixed">(job?.fee.type ?? "percent");
  const [feeValue, setFeeValue] = useState(job ? String(job.fee.type === "percent" ? job.fee.percent : job.fee.amount) : "");

  useEffect(() => {
    api
      .candidateSkills()
      .then((r) => setKnownSkills(r.skills.map((x) => x.skill)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    void Promise.all([
      api.contacts(companyId),
      api.users(),
      job ? Promise.resolve(null) : api.terms(companyId),
      job ? Promise.resolve(null) : Promise.all([api.me(), api.jobs({ companyId })]),
    ])
      .then(([c, u, t, overlap]) => {
        if (overlap) {
          const [me, open] = overlap;
          // Colleagues already working this client: say so before a second consultant starts.
          setColleagueJobs(open.jobs.filter((j) => (j.status === "open" || j.status === "on_hold") && j.owner.id !== me.user.id));
        }
        setContacts(c.contacts.filter((x) => !x.archived || x.id === job?.hiringContact?.id));
        setTeam(u.users.filter((x) => x.status === "active"));
        if (!job) {
          setTerms(t?.current ?? null);
          if (!t?.current) setCustomFee(true);
          else setCurrency(t.current.currency);
        }
      })
      .catch((err: Error) => setError(err.message));
  }, [companyId, job, setError]);

  const money = (v: string) => (v.trim() === "" ? null : Number(v.replace(/[£€$,\sk]/gi, "")) * (/k$/i.test(v.trim()) ? 1000 : 1));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const min = money(salaryMin);
    const max = money(salaryMax);
    if (Number.isNaN(min) || Number.isNaN(max)) return setError("Enter salaries as numbers, e.g. 80000 or 80k.");
    let fee: JobInput["fee"];
    if (customFee) {
      const value = Number(feeValue.replace(/[£€$,%\s]/g, ""));
      if (!Number.isFinite(value) || value <= 0) return setError("Enter the fee as a number.");
      fee = feeType === "percent" ? { type: "percent", percent: value } : { type: "fixed", amount: value };
      // When editing, only send the fee if it changed: sending it detaches the job from the terms.
      if (job && JSON.stringify(fee) === JSON.stringify(job.fee)) fee = undefined;
    }
    void run(() =>
      onSubmit({
        title: title.trim(),
        location: location.trim() || null,
        skills,
        salaryMin: min,
        salaryMax: max,
        currency,
        fee,
        hiringContactId: hiringContactId || null,
        ownerId: ownerId || undefined,
        notes,
        description,
      }),
    );
  };

  return (
    <form className="form panel" onSubmit={submit}>
      {colleagueJobs.length > 0 && (
        <p className="notice" role="status">
          Already being worked here:{" "}
          {colleagueJobs.map((j, i) => (
            <span key={j.id}>
              {i > 0 && "; "}
              <strong>{j.title}</strong> by {j.owner.name}
            </span>
          ))}
          . Check with them before pitching a new role.
        </p>
      )}
      <div className="grid-2">
        <Field label="Job title" required maxLength={200} autoFocus={!job} value={title} onChange={(e) => setTitle(e.target.value)} />
        <Field label="Location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. London, hybrid" />
      </div>
      <label className="field">
        <span className="label">Job description</span>
        <textarea
          rows={6}
          maxLength={50000}
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            setFound(null);
          }}
          placeholder="Paste the client's job description"
        />
      </label>
      <div className="button-row">
        <button
          type="button"
          className="link-btn"
          disabled={!description.trim() || picking.pending}
          onClick={() =>
            void picking.run(async () => {
              const { skills: inText } = await api.skillsFromText(description);
              setFound(inText.filter((s) => !skills.includes(s)));
            })
          }
        >
          {picking.pending ? "Reading…" : "Pick out skills"}
        </button>
        {found && found.length === 0 && <span className="hint">No new skills found. Add them below.</span>}
      </div>
      <ErrorNotice error={picking.error} />
      {found && found.length > 0 && (
        <div className="skill-filter" role="group" aria-label="Skills found in the description">
          <span className="label">Found in the description: tap to add</span>
          <div className="chips">
            {found.map((s) => (
              <button
                key={s}
                type="button"
                className="chip toggle"
                onClick={() => {
                  if (skills.length < 30) setSkills((cur) => (cur.includes(s) ? cur : [...cur, s]));
                  setFound((f) => f?.filter((x) => x !== s) ?? null);
                }}
              >
                + {displaySkill(s)}
              </button>
            ))}
            {found.length > 1 && (
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setSkills((cur) => [...cur, ...found.filter((s) => !cur.includes(s))].slice(0, 30));
                  setFound([]);
                }}
              >
                Add all
              </button>
            )}
          </div>
        </div>
      )}
      <SkillsInput label="Skills it needs" value={skills} onChange={setSkills} suggestions={knownSkills} />
      <p className="hint">Used to suggest candidates from your database and to build a web search for new ones.</p>
      <div className="grid-3">
        <Field label="Salary from" inputMode="numeric" value={salaryMin} onChange={(e) => setSalaryMin(e.target.value)} placeholder="80000 or 80k" />
        <Field label="Salary to" inputMode="numeric" value={salaryMax} onChange={(e) => setSalaryMax(e.target.value)} placeholder="95000 or 95k" />
        <label className="field">
          <span className="label">Currency</span>
          <select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)}>
            <option value="GBP">GBP £</option>
            <option value="EUR">EUR €</option>
            <option value="USD">USD $</option>
          </select>
        </label>
      </div>

      <div className="fee-block">
        <span className="label">Fee</span>
        {!customFee && terms ? (
          <p>
            <strong className="figures">{describeFee(terms)}</strong> from the agreed terms.{" "}
            <button type="button" className="link-btn" onClick={() => setCustomFee(true)}>
              Set a different fee for this job
            </button>
          </p>
        ) : (
          <>
            {!job && terms === null && <p className="hint">No terms recorded for this company, so set the fee here.</p>}
            <div className="grid-3">
              <label className="field">
                <span className="label">Type</span>
                <select value={feeType} onChange={(e) => setFeeType(e.target.value as "percent" | "fixed")}>
                  <option value="percent">% of salary</option>
                  <option value="fixed">Fixed amount</option>
                </select>
              </label>
              <Field
                label={feeType === "percent" ? "Fee %" : "Amount"}
                inputMode="decimal"
                required
                value={feeValue}
                onChange={(e) => setFeeValue(e.target.value)}
              />
              {!job && terms && (
                <button type="button" className="link-btn align-end" onClick={() => setCustomFee(false)}>
                  Use the agreed terms
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <div className="grid-2">
        <label className="field">
          <span className="label">Hiring contact</span>
          <select value={hiringContactId} onChange={(e) => setHiringContactId(e.target.value)}>
            <option value="">— Not set —</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.fullName}
                {c.jobTitle ? ` (${c.jobTitle})` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Owner</span>
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            {!job && <option value="">Me</option>}
            {team.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName ?? u.email}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="field">
        <span className="label">Notes</span>
        <textarea rows={4} maxLength={20000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Must-haves, selling points, process quirks…" />
      </label>

      <ErrorNotice error={error} />
      <div className="button-row">
        <button className="btn" disabled={pending}>
          {pending ? "Saving…" : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="link-btn" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
